import { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";

type DeleteScheduleDialogProps = {
  open: boolean;
  row: any;
  deleting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const formatTimeLeft = (ms: number) => {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const DeleteScheduleDialog = (props: DeleteScheduleDialogProps) => {
  const { open, row, deleting, onClose, onConfirm } = props;

  const [now, setNow] = useState(Date.now());

  // keep the countdown fresh while the dialog is open
  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [open]);

  const scheduledStartTime = new Date(row?.liveSession?.scheduledStartTime).getTime();

  const className = row?.meetingTitle || "this class";
  const studentCount = row?.scheduledParticipantsCount ?? 0;

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
          p: 1.5,
        },
      }}
    >
      <DialogContent sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#0f172a", mb: 1 }}>
          Delete this scheduled class?
        </Typography>
        <Typography sx={{ fontSize: "16px", color: "#64748b", lineHeight: 1.6 }}>
          This action is allowed only before the class starts. Deleting {className} will also
          remove its student mapping ({studentCount} students). This cannot be undone.
        </Typography>

        <Box
          sx={{
            mt: 2.5,
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "#effcf3",
            border: "1px solid #b7efc8",
            borderRadius: 2,
            color: "#14532d",
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#14532d", flexShrink: 0 }} />
          <Typography sx={{ fontSize: "15px" }}>
            Class starts in <b>{formatTimeLeft(scheduledStartTime - now)}</b> — delete is
            currently allowed.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={deleting}
          sx={{
            textTransform: "none",
            fontSize: "16px",
            borderRadius: 2,
            px: 3,
            py: 1,
            color: "#0f172a",
            bgcolor: "#fff",
            borderColor: "#e2e8f0",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={deleting}
          sx={{
            textTransform: "none",
            fontSize: "16px",
            borderRadius: 2,
            px: 3,
            py: 1,
            bgcolor: "#dc3545",
            boxShadow: "none",
            "&:hover": { bgcolor: "#c82333", boxShadow: "none" },
          }}
        >
          {deleting ? "Deleting..." : "Delete Class"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
